import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  useActualizarUsuario,
  useCrearUsuario,
  useResetPassword,
  useUsuarios,
} from '../api/queries-admin';
import { FinanceDemoNotice } from '../components/FinanceBits';
import { Modal } from '../components/Modal';
import { PasswordStrength } from '../components/PasswordStrength';
import { EyeIcon, PlusIcon } from '../components/icons';
import { useToast } from '../ui/ToastProvider';
import { apiErrorMessage } from '../lib/apiError';
import { formatRelative, roleLabel } from '../lib/format';
import { passwordIssues } from '../lib/passwordPolicy';
import type { Rol, UsuarioAdmin } from '../api/types';

const ROLES: Array<{ value: Rol; label: string }> = [
  { value: 'admin', label: 'Administrador' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'ventas', label: 'Ventas' },
  { value: 'cobranza', label: 'Cobranzas' },
  { value: 'auditor', label: 'Auditor' },
];

interface UsuarioForm {
  email: string;
  nombre: string;
  password: string;
  rol: Rol;
  vendedorCodigo: string;
  telefono: string;
  activo: boolean;
}

const EMPTY_FORM: UsuarioForm = {
  email: '',
  nombre: '',
  password: '',
  rol: 'ventas',
  vendedorCodigo: '',
  telefono: '',
  activo: true,
};

export function UsuariosPage() {
  const { user, demo } = useAuth();
  const { notify } = useToast();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<UsuarioAdmin | null>(null);
  const [resetting, setResetting] = useState<UsuarioAdmin | null>(null);
  const [form, setForm] = useState<UsuarioForm>(EMPTY_FORM);
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const usuarios = useUsuarios(search);
  const crear = useCrearUsuario();
  const actualizar = useActualizarUsuario();
  const reset = useResetPassword();
  const rows = usuarios.data ?? [];
  const isAdmin = !demo && user?.rol === 'admin';

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setPasswordConfirm('');
    setShowCreatePassword(false);
    setCreateError(null);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateError(null);
    setPasswordConfirm('');
  };

  const submitCreate = async () => {
    if (!form.email.trim() || !form.nombre.trim()) {
      setCreateError('Correo y nombre son obligatorios.');
      notify('Correo y nombre son obligatorios.', 'warning');
      return;
    }
    const issues = passwordIssues(form.password, {
      email: form.email,
      nombre: form.nombre,
    });
    if (issues.length > 0) {
      setCreateError(`Contraseña insegura: ${issues[0]}.`);
      notify('La contraseña no cumple la política de seguridad.', 'warning');
      return;
    }
    if (form.password !== passwordConfirm) {
      setCreateError('Las contraseñas no coinciden.');
      notify('Las contraseñas no coinciden.', 'warning');
      return;
    }

    setCreateError(null);
    try {
      await crear.mutateAsync({
        email: form.email.trim(),
        nombre: form.nombre.trim(),
        password: form.password,
        rol: form.rol,
        ...(form.vendedorCodigo ? { vendedorCodigo: form.vendedorCodigo } : {}),
        ...(form.telefono ? { telefono: form.telefono } : {}),
      });
      notify(`Usuario ${form.nombre.trim()} creado. Ya puede entrar con su correo o código.`, 'success');
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      setPasswordConfirm('');
    } catch (error) {
      setCreateError(apiErrorMessage(error, 'No se pudo crear el usuario.'));
      notify(apiErrorMessage(error, 'No se pudo crear el usuario.'), 'error');
    }
  };

  const submitEdit = async () => {
    if (!editing || !form.nombre.trim()) {
      notify('El nombre es obligatorio.', 'warning');
      return;
    }
    try {
      await actualizar.mutateAsync({
        id: editing.id,
        payload: {
          nombre: form.nombre.trim(),
          rol: form.rol,
          vendedorCodigo: form.vendedorCodigo.trim() || null,
          telefono: form.telefono.trim() || null,
          activo: form.activo,
        },
      });
      notify(`Usuario ${form.nombre.trim()} actualizado.`, 'success');
      setEditing(null);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo actualizar.', 'error');
    }
  };

  const toggleActivo = async (row: UsuarioAdmin) => {
    try {
      await actualizar.mutateAsync({
        id: row.id,
        payload: { activo: !row.activo },
      });
      notify(
        `Usuario ${row.nombre} ${row.activo ? 'desactivado' : 'activado'}.`,
        'success',
      );
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo cambiar el estado.', 'error');
    }
  };

  const submitReset = async () => {
    if (!resetting) return;
    const issues = passwordIssues(newPassword, {
      email: resetting.email,
      nombre: resetting.nombre,
    });
    if (issues.length > 0) {
      setResetError(`Contraseña insegura: ${issues[0]}.`);
      notify('La nueva contraseña no cumple la política de seguridad.', 'warning');
      return;
    }
    if (newPassword !== resetPasswordConfirm) {
      setResetError('Las contraseñas no coinciden.');
      notify('Las contraseñas no coinciden.', 'warning');
      return;
    }

    setResetError(null);
    try {
      await reset.mutateAsync({ id: resetting.id, password: newPassword });
      notify(`Contraseña de ${resetting.nombre} actualizada; sesiones revocadas.`, 'success');
      setResetting(null);
      setNewPassword('');
      setResetPasswordConfirm('');
    } catch (error) {
      const message = apiErrorMessage(error, 'No se pudo cambiar la contraseña.');
      setResetError(message);
      notify(message, 'error');
    }
  };

  const openEdit = (row: UsuarioAdmin) => {
    setEditing(row);
    setForm({
      email: row.email,
      nombre: row.nombre,
      password: '',
      rol: row.rol,
      vendedorCodigo: row.vendedorCodigo ?? '',
      telefono: row.telefono ?? '',
      activo: row.activo,
    });
  };

  return (
    <div className="dashboard">
      <header className="page-head">
        <div>
          <p className="eyebrow">Administración</p>
          <h1 className="page-title">Usuarios & Roles</h1>
          <p className="page-sub">
            Altas, roles, códigos de acceso, activación y rotación de contraseñas del equipo.
          </p>
        </div>
        <button
          type="button"
          className="btn btn--primary"
          disabled={!isAdmin}
          onClick={openCreate}
        >
          <PlusIcon size={17} /> Nuevo usuario
        </button>
      </header>

      <FinanceDemoNotice demo={demo} />

      <section className="card clients-card">
        <div className="clients-toolbar">
          <div className="search-input">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, correo o código…"
              aria-label="Buscar usuarios"
            />
          </div>
          <span className="pill pill--soft">{rows.length} usuarios</span>
        </div>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Rol</th>
                <th>Código de usuario</th>
                <th>Teléfono</th>
                <th>Último acceso</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {usuarios.isLoading ? (
                <tr><td colSpan={7}><div className="table-empty">Cargando usuarios…</div></td></tr>
              ) : null}
              {!usuarios.isLoading && rows.length === 0 ? (
                <tr><td colSpan={7}><div className="table-empty">Sin usuarios para mostrar.</div></td></tr>
              ) : null}
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div className="doc-cell">
                      <strong>
                        {row.nombre}
                        {row.id === user?.id ? ' (tú)' : ''}
                      </strong>
                      <span>{row.email}</span>
                    </div>
                  </td>
                  <td><span className="pill pill--soft">{roleLabel(row.rol)}</span></td>
                  <td>{row.vendedorCodigo ?? '—'}</td>
                  <td>{row.telefono ?? '—'}</td>
                  <td>{row.lastLoginAt ? formatRelative(row.lastLoginAt) : 'Nunca'}</td>
                  <td>
                    <span className={`status-badge status-badge--${row.activo ? 'success' : 'neutral'}`}>
                      {row.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="btn btn--outline btn--sm"
                        disabled={!isAdmin}
                        onClick={() => openEdit(row)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={!isAdmin}
                        onClick={() => {
                          setResetting(row);
                          setNewPassword('');
                          setResetPasswordConfirm('');
                          setShowResetPassword(false);
                          setResetError(null);
                        }}
                      >
                        Contraseña
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={!isAdmin || row.id === user?.id}
                        onClick={() => void toggleActivo(row)}
                      >
                        {row.activo ? 'Desactivar' : 'Activar'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {createOpen ? (
        <Modal
          title="Nuevo usuario"
          eyebrow="Equipo"
          onClose={closeCreate}
          footer={
            <>
              <button type="button" className="btn btn--outline" onClick={closeCreate}>Cancelar</button>
              <button type="button" className="btn btn--primary" onClick={() => void submitCreate()} disabled={crear.isPending}>
                {crear.isPending ? 'Creando…' : 'Crear usuario'}
              </button>
            </>
          }
        >
          <div className="modal-card__body">
            {createError ? (
              <div className="form-error" role="alert">{createError}</div>
            ) : null}
            <div className="form-grid">
              <label className="field">
                <span className="field-label">Correo *</span>
                <input
                  className="input"
                  type="email"
                  autoComplete="off"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="nombre@empresa.com"
                />
              </label>
              <label className="field">
                <span className="field-label">Nombre completo *</span>
                <input className="input" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Nombre y apellido" />
              </label>
              <label className="field">
                <span className="field-label">Contraseña inicial *</span>
                <div className="password-field">
                  <input
                    className="input"
                    type={showCreatePassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(e) => {
                      setForm({ ...form, password: e.target.value });
                      setCreateError(null);
                    }}
                    placeholder="Crea una contraseña fuerte"
                  />
                  <button
                    type="button"
                    className="icon-button icon-button--tiny"
                    onClick={() => setShowCreatePassword((current) => !current)}
                    aria-label={showCreatePassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    <EyeIcon size={17} />
                  </button>
                </div>
              </label>
              <label className="field">
                <span className="field-label">Repetir contraseña *</span>
                <input
                  className="input"
                  type={showCreatePassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder="Confirma la contraseña"
                />
              </label>
              <div className="field field--wide">
                <PasswordStrength
                  password={form.password}
                  context={{ email: form.email, nombre: form.nombre }}
                />
              </div>
              <label className="field">
                <span className="field-label">Rol *</span>
                <select className="input" value={form.rol} onChange={(e) => setForm({ ...form, rol: e.target.value as Rol })}>
                  {ROLES.map((rol) => <option key={rol.value} value={rol.value}>{rol.label}</option>)}
                </select>
              </label>
              <label className="field">
                <span className="field-label">Código de usuario</span>
                <input
                  className="input"
                  value={form.vendedorCodigo}
                  onChange={(e) => setForm({ ...form, vendedorCodigo: e.target.value })}
                  placeholder="Ej.: admin02"
                  autoCapitalize="none"
                />
                <span className="field-help">Sirve para iniciar sesión sin escribir el correo.</span>
              </label>
              <label className="field field--wide">
                <span className="field-label">Teléfono</span>
                <input className="input" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="Opcional" />
              </label>
            </div>
          </div>
        </Modal>
      ) : null}

      {editing ? (
        <Modal
          title={`Editar ${editing.nombre}`}
          eyebrow={editing.email}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button type="button" className="btn btn--outline" onClick={() => setEditing(null)}>Cancelar</button>
              <button type="button" className="btn btn--primary" onClick={() => void submitEdit()} disabled={actualizar.isPending}>
                {actualizar.isPending ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </>
          }
        >
          <div className="modal-card__body">
            <div className="form-grid">
              <label className="field"><span className="field-label">Nombre *</span><input className="input" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></label>
              <label className="field"><span className="field-label">Rol</span><select className="input" value={form.rol} onChange={(e) => setForm({ ...form, rol: e.target.value as Rol })} disabled={editing.id === user?.id}>{ROLES.map((rol) => <option key={rol.value} value={rol.value}>{rol.label}</option>)}</select></label>
              <label className="field"><span className="field-label">Código de usuario</span><input className="input" value={form.vendedorCodigo} onChange={(e) => setForm({ ...form, vendedorCodigo: e.target.value })} /></label>
              <label className="field"><span className="field-label">Teléfono</span><input className="input" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></label>
              <label className="checkbox field--wide">
                <input type="checkbox" checked={form.activo} disabled={editing.id === user?.id} onChange={(e) => setForm({ ...form, activo: e.target.checked })} />
                Usuario activo
              </label>
            </div>
            <p className="page-footnote">El correo no se edita desde aquí para preservar la identidad de auditoría.</p>
          </div>
        </Modal>
      ) : null}

      {resetting ? (
        <Modal
          title={`Cambiar contraseña de ${resetting.nombre}`}
          eyebrow="Seguridad"
          onClose={() => {
            setResetting(null);
            setResetError(null);
            setResetPasswordConfirm('');
          }}
          footer={
            <>
              <button
                type="button"
                className="btn btn--outline"
                onClick={() => {
                  setResetting(null);
                  setResetError(null);
                  setResetPasswordConfirm('');
                }}
              >
                Cancelar
              </button>
              <button type="button" className="btn btn--primary" onClick={() => void submitReset()} disabled={reset.isPending}>
                {reset.isPending ? 'Guardando…' : 'Cambiar y revocar sesiones'}
              </button>
            </>
          }
        >
          <div className="modal-card__body">
            {resetError ? (
              <div className="form-error" role="alert">{resetError}</div>
            ) : null}
            <div className="form-grid">
              <label className="field">
                <span className="field-label">Nueva contraseña *</span>
                <div className="password-field">
                  <input
                    className="input"
                    type={showResetPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setResetError(null);
                    }}
                    placeholder="Crea una contraseña fuerte"
                  />
                  <button
                    type="button"
                    className="icon-button icon-button--tiny"
                    onClick={() => setShowResetPassword((current) => !current)}
                    aria-label={showResetPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    <EyeIcon size={17} />
                  </button>
                </div>
              </label>
              <label className="field">
                <span className="field-label">Repetir contraseña *</span>
                <input
                  className="input"
                  type={showResetPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={resetPasswordConfirm}
                  onChange={(e) => setResetPasswordConfirm(e.target.value)}
                  placeholder="Confirma la contraseña"
                />
              </label>
              <div className="field field--wide">
                <PasswordStrength
                  password={newPassword}
                  context={{ email: resetting.email, nombre: resetting.nombre }}
                />
              </div>
            </div>
            <p className="inline-note">Al cambiarla se revocan todos los refresh tokens del usuario.</p>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
